import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Building2, Users, User, Calendar, ExternalLink, Search, Plus } from 'lucide-react';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ViewToggle, ViewMode } from '@/components/common/ViewToggle';
import { RoleRow, RoleCompanyBreakdown, SENIORITY_LEVELS, SeniorityBreakdown, SeniorityLevel } from '@/utils/rolesGrouping';
import { SeniorityPills } from './SeniorityPills';
import { AddCompanyForm } from '@/components/companies/AddCompanyForm';
import { useCreateCompany } from '@/hooks/companies/useCreateCompany';

interface RolesListProps {
	roles: RoleRow[];
	loading: boolean;
	error: string | null;
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	viewMode: ViewMode;
	setViewMode: (mode: ViewMode) => void;
	onCompanyCreated?: () => void;
}

const getIntensityColor = (ratio: number): string => {
	const pct = ratio * 100;
	if (pct >= 80) return 'bg-blue-700';
	if (pct >= 60) return 'bg-blue-500';
	if (pct >= 40) return 'bg-sky-500';
	if (pct >= 20) return 'bg-cyan-400';
	return 'bg-teal-300';
};

const CompanyBreakdownRow = ({
	breakdown,
	roleTitle,
	maxCount,
	onCompanyCreated,
}: {
	breakdown: RoleCompanyBreakdown;
	roleTitle: string;
	maxCount: number;
	onCompanyCreated?: () => void;
}) => {
	const ratio = maxCount > 0 ? breakdown.candidateCount / maxCount : 0;
	const color = getIntensityColor(ratio);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const { createCompany } = useCreateCompany();

	const handleSubmit = async (payload: Parameters<typeof createCompany>[0]) => {
		const result = await createCompany(payload);
		if (result.success) {
			setIsCreateOpen(false);
			onCompanyCreated?.();
		}
		return result.success;
	};

	return (
		<>
			<Popover>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
					>
						<div className={`w-2.5 h-2.5 rounded-full shrink-0 ${color}`} />
						<span className="flex-1 font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
							{breakdown.company}
						</span>
						<SeniorityPills breakdown={breakdown.seniorityBreakdown} />
						<span className="text-sm font-semibold text-gray-700 dark:text-gray-200 tabular-nums w-8 text-right">
							{breakdown.candidateCount}
						</span>
					</button>
				</PopoverTrigger>
				<PopoverContent
					className="w-96 max-h-[60vh] overflow-hidden p-0"
					side="bottom"
					align="start"
					sideOffset={4}
					avoidCollisions={true}
					collisionPadding={16}
				>
					<div className="p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
						<div className="flex items-center justify-between gap-2">
							<div className="flex items-center gap-2 min-w-0">
								<Building2 className="h-4 w-4 text-blue-500 shrink-0" />
								<h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
									{breakdown.company}
								</h4>
							</div>
							{breakdown.companyId ? (
								<Link to={`/companies/${breakdown.companyId}`} className="shrink-0">
									<Button variant="outline" size="sm">
										<ExternalLink className="h-3 w-3 mr-1" />
										Open
									</Button>
								</Link>
							) : (
								<Button
									variant="outline"
									size="sm"
									className="shrink-0"
									onClick={(e) => {
										e.stopPropagation();
										setIsCreateOpen(true);
									}}
								>
									<Plus className="h-3 w-3 mr-1" />
									Create
								</Button>
							)}
						</div>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
							{breakdown.candidateCount} × {roleTitle}
						</p>
					</div>
					<div className="max-h-[50vh] overflow-y-auto p-2 space-y-1">
						{breakdown.candidates.map(candidate => (
							<div
								key={`${candidate.id}-${candidate.experienceId}`}
								className="flex items-center justify-between gap-2 p-2 rounded-sm hover:bg-gray-50 dark:hover:bg-gray-800"
							>
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-1.5">
										<User className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
										<span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
											{candidate.firstName || 'Unknown'}
										</span>
									</div>
									<div className="flex items-center gap-1.5 mt-0.5">
										<Briefcase className="h-3 w-3 text-gray-400 shrink-0" />
										<span className="text-xs text-gray-600 dark:text-gray-400 truncate">
											{candidate.originalTitle}
										</span>
									</div>
									{candidate.dates && (
										<div className="flex items-center gap-1.5 mt-0.5">
											<Calendar className="h-3 w-3 text-gray-400 shrink-0" />
											<span className="text-xs text-gray-500 dark:text-gray-400 truncate">
												{candidate.dates}
											</span>
										</div>
									)}
								</div>
								<Link to={`/candidates/${candidate.id}`}>
									<Button variant="outline" size="sm" className="shrink-0">
										<ExternalLink className="h-3 w-3 mr-1" />
										View
									</Button>
								</Link>
							</div>
						))}
					</div>
				</PopoverContent>
			</Popover>
			<AddCompanyForm
				isOpen={isCreateOpen}
				onClose={() => setIsCreateOpen(false)}
				onSubmit={handleSubmit}
				initialCompany={{ name: breakdown.company, relationship: 'prospect' }}
			/>
		</>
	);
};

const emptyBreakdown = (): SeniorityBreakdown => ({ lead: 0, senior: 0, mid: 0, junior: 0, unknown: 0 });

const RoleCard = ({
	role,
	onCompanyCreated,
}: {
	role: RoleRow;
	onCompanyCreated?: () => void;
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeLevels, setActiveLevels] = useState<Set<SeniorityLevel>>(
		() => new Set(SENIORITY_LEVELS)
	);
	const allActive = activeLevels.size === SENIORITY_LEVELS.length;

	const toggleLevel = (level: SeniorityLevel) => {
		setActiveLevels(prev => {
			if (prev.size === SENIORITY_LEVELS.length)
				return new Set([level]);
			const next = new Set(prev);
			if (next.has(level)) {
				next.delete(level);
				if (next.size === 0)
					return new Set(SENIORITY_LEVELS);
			} else {
				next.add(level);
			}
			return next;
		});
	};

	const clearFilter = () => setActiveLevels(new Set(SENIORITY_LEVELS));

	const filteredCompanies = allActive
		? role.companyBreakdown
		: role.companyBreakdown
			.map(b => {
				const newBd = emptyBreakdown();
				let count = 0;
				SENIORITY_LEVELS.forEach(l => {
					if (activeLevels.has(l)) {
						newBd[l] = b.seniorityBreakdown[l];
						count += b.seniorityBreakdown[l];
					}
				});
				return {
					...b,
					seniorityBreakdown: newBd,
					candidateCount: count,
					candidates: b.candidates.filter(c => activeLevels.has(c.seniority)),
				};
			})
			.filter(b => b.candidateCount > 0)
			.sort((a, b) => b.candidateCount - a.candidateCount);

	const maxInRole = filteredCompanies[0]?.candidateCount || 0;
	const topCompanies = filteredCompanies.slice(0, 5);
	const remaining = filteredCompanies.length - topCompanies.length;
	const totalShown = allActive
		? role.totalCandidateCount
		: filteredCompanies.reduce((s, b) => s + b.candidateCount, 0);

	return (
		<>
			<Card className="flex flex-col">
				<CardHeader className="pb-3">
					<div className="flex items-start justify-between gap-2">
						<CardTitle className="text-base flex items-center gap-2 min-w-0">
							<Briefcase className="h-4 w-4 text-blue-500 shrink-0" />
							<span className="truncate">{role.title}</span>
						</CardTitle>
						<div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 shrink-0">
							<Users className="h-4 w-4" />
							<span className="font-semibold tabular-nums">{totalShown}</span>
						</div>
					</div>
					<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
						{filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
					</p>
					<div className="mt-2">
						<SeniorityPills
							breakdown={role.seniorityBreakdown}
							size="sm"
							activeLevels={activeLevels}
							onToggle={toggleLevel}
							onClear={clearFilter}
						/>
					</div>
				</CardHeader>
				<CardContent className="pt-0 space-y-1">
					{topCompanies.map(breakdown => (
						<CompanyBreakdownRow
							key={breakdown.company}
							breakdown={breakdown}
							roleTitle={role.title}
							maxCount={maxInRole}
							onCompanyCreated={onCompanyCreated}
						/>
					))}
					{remaining > 0 && (
						<button
							type="button"
							onClick={() => setIsModalOpen(true)}
							className="text-xs text-blue-600 dark:text-blue-400 hover:underline pt-1 pl-3 text-left"
						>
							+{remaining} more {remaining === 1 ? 'company' : 'companies'}
						</button>
					)}
				</CardContent>
			</Card>
			<Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
				<DialogContent className="max-w-4xl w-[90vw] max-h-[85vh] overflow-hidden p-0 flex flex-col gap-0">
					<DialogHeader className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900 space-y-0">
						<div className="flex items-center gap-2 min-w-0 pr-8">
							<Briefcase className="h-4 w-4 text-blue-500 shrink-0" />
							<DialogTitle className="text-base truncate">{role.title}</DialogTitle>
						</div>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
							{filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'} · {totalShown} candidates
						</p>
					</DialogHeader>
					<div className="overflow-y-auto p-2 space-y-1">
						{filteredCompanies.map(breakdown => (
							<CompanyBreakdownRow
								key={breakdown.company}
								breakdown={breakdown}
								roleTitle={role.title}
								maxCount={maxInRole}
								onCompanyCreated={onCompanyCreated}
							/>
						))}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export const RolesList = ({ roles, loading, error, searchTerm, setSearchTerm, viewMode, setViewMode, onCompanyCreated }: RolesListProps) => {
	if (error) {
		return (
			<div className="bg-white dark:bg-gray-900 rounded-sm shadow p-6">
				<div className="text-center text-red-600">
					<p>{error}</p>
				</div>
			</div>
		);
	}

	const totalRoles = roles.length;
	const totalCandidates = roles.reduce((sum, r) => sum + r.totalCandidateCount, 0);

	return (
		<div className="bg-white dark:bg-gray-900 rounded-sm shadow">
			<div className="p-6">
				<div className="flex items-center justify-between mb-4 gap-4">
					<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
						<Briefcase className="h-5 w-5 text-gray-600 dark:text-gray-400" />
						Roles
					</h3>
					<div className="flex items-center gap-2">
						<div className="relative w-72 max-w-full">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
							<input
								type="text"
								placeholder="Search role, company, candidate..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="w-full pl-9 pr-3 py-2 border rounded-sm text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
							/>
						</div>
						<ViewToggle
							viewMode={viewMode}
							onViewModeChange={setViewMode}
							label=""
							allowedModes={['list', 'card']}
						/>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-3 mb-6">
					<Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
						<CardContent className="p-3">
							<div className="flex items-center space-x-2">
								<div className="p-1.5 bg-blue-500 rounded-sm">
									<Briefcase className="h-4 w-4 text-white" />
								</div>
								<div>
									<p className="text-xs text-blue-500 font-medium">Roles</p>
									<p className="text-lg font-bold text-blue-500">{totalRoles}</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
						<CardContent className="p-3">
							<div className="flex items-center space-x-2">
								<div className="p-1.5 bg-green-500 rounded-sm">
									<Users className="h-4 w-4 text-white" />
								</div>
								<div>
									<p className="text-xs text-green-600 font-medium">Candidates</p>
									<p className="text-lg font-bold text-green-700">{totalCandidates}</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				{loading ? (
					<div className="animate-pulse space-y-4">
						{[1, 2, 3].map(i => (
							<div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-sm" />
						))}
					</div>
				) : roles.length > 0 ? (
					viewMode === 'list' ? (
						<Accordion type="multiple" className="w-full">
							{roles.map(role => {
								const maxInRole = role.companyBreakdown[0]?.candidateCount || 0;
								return (
									<AccordionItem key={role.title} value={role.title}>
										<AccordionTrigger className="hover:no-underline">
											<div className="flex items-center justify-between w-full pr-4 gap-3">
												<div className="flex items-center gap-2 min-w-0">
													<Briefcase className="h-4 w-4 text-blue-500 shrink-0" />
													<span className="font-medium truncate text-left">{role.title}</span>
												</div>
												<div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 shrink-0">
													<SeniorityPills breakdown={role.seniorityBreakdown} size="sm" />
													<span className="text-xs">
														{role.companyBreakdown.length} {role.companyBreakdown.length === 1 ? 'company' : 'companies'}
													</span>
													<div className="flex items-center gap-1">
														<Users className="h-4 w-4" />
														<span className="font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
															{role.totalCandidateCount}
														</span>
													</div>
												</div>
											</div>
										</AccordionTrigger>
										<AccordionContent>
											<div className="space-y-1 pt-2">
												{role.companyBreakdown.map(breakdown => (
													<CompanyBreakdownRow
														key={breakdown.company}
														breakdown={breakdown}
														roleTitle={role.title}
														maxCount={maxInRole}
														onCompanyCreated={onCompanyCreated}
													/>
												))}
											</div>
										</AccordionContent>
									</AccordionItem>
								);
							})}
						</Accordion>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
							{roles.map(role => (
								<RoleCard
									key={role.title}
									role={role}
									onCompanyCreated={onCompanyCreated}
								/>
							))}
						</div>
					)
				) : (
					<div className="text-center py-8 border dark:border-gray-700 rounded-sm">
						<Briefcase className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto" />
						<p className="mt-2 text-gray-500 dark:text-gray-400">No roles found</p>
					</div>
				)}
			</div>
		</div>
	);
};
